import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Tooltip,
  Stack,
  Tabs,
  Tab,
  FormControlLabel,
  Switch,
  Divider,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  BugReport as BugIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { formatDistanceToNow } from 'date-fns';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import { apiFetch } from '../services/httpClient';

const API_BASE = process.env.REACT_APP_API_URL || '';

// Auth is httpOnly-cookie based (see AuthContext/api.js) — the browser sends the
// session cookie automatically on same-origin requests. Only attach an
// Authorization header if a legacy localStorage token actually exists; sending a
// synthesized "Bearer null" would make the server reject an otherwise valid request.
const getAuthToken = () => {
  const token = localStorage.getItem('authToken') || localStorage.getItem('token');
  return token && token !== 'null' && token !== 'undefined' ? token : null;
};

const getAuthHeaders = (includeJsonContentType = false) => {
  const token = getAuthToken();
  const headers = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (includeJsonContentType) {
    headers['Content-Type'] = 'application/json';
  }
  return headers;
};

const CRASH_TYPE_LABELS = {
  exception: 'Exception',
  signal: 'Signal',
  hang: 'Hang',
  memory_pressure: 'Memory Pressure',
  unknown: 'Unknown',
};

const CRASH_TYPE_COLORS = {
  exception: 'error',
  signal: 'error',
  hang: 'warning',
  memory_pressure: 'warning',
  unknown: 'default',
};

const REVIEW_STATUS_LABELS = {
  new: 'New',
  investigating: 'Investigating',
  fixed: 'Fixed',
  wont_fix: "Won't Fix",
};

const REVIEW_STATUS_COLORS = {
  new: 'error',
  investigating: 'warning',
  fixed: 'success',
  wont_fix: 'default',
};

const formatWhen = (value) => {
  if (!value) return '—';
  try {
    return formatDistanceToNow(new Date(value), { addSuffix: true });
  } catch {
    return '—';
  }
};

const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return '—';
  }
};

/** A group of reports sharing one crash signature — one bug, every company it hit. */
const CrashGroupDialog = ({ group, onClose }) => {
  const queryClient = useQueryClient();
  const signature = group.signature;
  const [reviewStatus, setReviewStatus] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [editing, setEditing] = useState(false);

  const { data: groupData, isLoading: groupLoading } = useQuery(
    ['crash-reports', 'signature', signature, 1, 25],
    () =>
      apiFetch(
        `${API_BASE}/api/crash-reports/signature/${signature}?limit=25&page=1`
      ).then((res) => res),
    { enabled: Boolean(signature) }
  );

  const items = groupData?.items || [];
  const sampleReportId = items[0]?._id;

  const { data: detailData, isLoading: detailLoading } = useQuery(
    ['crash-reports', 'detail', sampleReportId],
    () => apiFetch(`${API_BASE}/api/crash-reports/${sampleReportId}`),
    { enabled: Boolean(sampleReportId) }
  );

  const report = detailData?.report;

  const saveMutation = useMutation(
    ({ status, notes }) =>
      apiFetch(`${API_BASE}/api/crash-reports/signature/${signature}`, {
        method: 'PATCH',
        headers: getAuthHeaders(true),
        body: JSON.stringify({ reviewStatus: status, reviewNotes: notes }),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['crash-reports']);
        setEditing(false);
      },
    }
  );

  const startEditing = () => {
    setReviewStatus(group.reviewStatus || 'new');
    setReviewNotes(report?.reviewNotes || '');
    setEditing(true);
  };

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <BugIcon color={report?.isFatal ? 'error' : 'warning'} />
        <Box sx={{ flexGrow: 1 }}>
          {report?.exceptionName || report?.signal || CRASH_TYPE_LABELS[report?.crashType] || 'Crash'}
          <Typography variant="caption" display="block" color="text.secondary">
            {signature}
          </Typography>
        </Box>
        {report && (
          <Chip
            size="small"
            label={report.isFatal ? 'Fatal' : 'Non-fatal'}
            color={report.isFatal ? 'error' : 'default'}
          />
        )}
      </DialogTitle>

      <DialogContent dividers>
        {groupLoading || detailLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Triage
              </Typography>
              {editing ? (
                <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mt: 1 }}>
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      label="Status"
                      value={reviewStatus}
                      onChange={(e) => setReviewStatus(e.target.value)}
                    >
                      {Object.entries(REVIEW_STATUS_LABELS).map(([value, label]) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    fullWidth
                    label="Notes"
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                  />
                  <Button
                    variant="contained"
                    disabled={saveMutation.isLoading}
                    onClick={() => saveMutation.mutate({ status: reviewStatus, notes: reviewNotes })}
                  >
                    Save
                  </Button>
                  <Button onClick={() => setEditing(false)}>Cancel</Button>
                </Stack>
              ) : (
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
                  <Chip
                    label={REVIEW_STATUS_LABELS[group.reviewStatus] || group.reviewStatus || 'New'}
                    color={REVIEW_STATUS_COLORS[group.reviewStatus] || 'default'}
                  />
                  <Typography variant="body2" sx={{ flexGrow: 1 }}>
                    {report?.reviewNotes || 'No notes yet.'}
                  </Typography>
                  <Button size="small" startIcon={<EditIcon />} onClick={startEditing}>
                    Change
                  </Button>
                </Stack>
              )}
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                One decision applies to every report of this crash.
              </Typography>
            </Box>

            <Divider />

            <Grid container spacing={2}>
              {[
                ['Reports', `${group.reportCount ?? items.length}`],
                ['Companies', `${group.companyCount ?? '—'}`],
                ['App versions', (group.versions || []).filter(Boolean).join(', ') || '—'],
                ['iOS versions', (group.iosVersions || []).filter(Boolean).join(', ') || '—'],
                ['First seen', formatDateTime(group.firstOccurredAt)],
                ['Last seen', formatDateTime(group.lastOccurredAt)],
                ['Device', group.deviceModel || '—'],
              ].map(([label, value]) => (
                <Grid item xs={6} md={4} lg={3} key={label}>
                  <Typography variant="caption" color="text.secondary">
                    {label}
                  </Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                    {value}
                  </Typography>
                </Grid>
              ))}
            </Grid>

            {report?.exceptionReason && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Reason
                </Typography>
                <Typography variant="body2">{report.exceptionReason}</Typography>
              </Box>
            )}

            {report?.userComment && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  What the user reported
                </Typography>
                <Typography variant="body2">{report.userComment}</Typography>
              </Box>
            )}

            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Stack trace
              </Typography>
              <Paper
                variant="outlined"
                sx={{ p: 2, maxHeight: 320, overflow: 'auto', bgcolor: 'grey.50' }}
              >
                <Typography
                  component="pre"
                  sx={{ m: 0, fontSize: 12, fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}
                >
                  {report?.stackTrace || 'No stack trace available.'}
                </Typography>
              </Paper>
            </Box>

            {Array.isArray(report?.breadcrumbs) && report.breadcrumbs.length > 0 && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Last actions before the crash ({report.breadcrumbs.length})
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 240 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>When</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {report.breadcrumbs.map((crumb, index) => (
                        <TableRow key={`${crumb.timestamp || index}-${index}`}>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {formatDateTime(crumb.timestamp)}
                          </TableCell>
                          <TableCell>{crumb.category || '—'}</TableCell>
                          <TableCell>{crumb.message || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Every report of this crash
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Company</TableCell>
                      <TableCell>App</TableCell>
                      <TableCell>iOS</TableCell>
                      <TableCell>Device</TableCell>
                      <TableCell>When</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item) => (
                      <TableRow key={item._id}>
                        <TableCell>{item.companyName || item.companyId}</TableCell>
                        <TableCell>{item.appVersion || '—'}</TableCell>
                        <TableCell>{item.iosVersion || '—'}</TableCell>
                        <TableCell>{item.deviceModel || '—'}</TableCell>
                        <TableCell>{formatWhen(item.occurredAt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const CrashReports = () => {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [crashType, setCrashType] = useState('');
  const [reviewStatus, setReviewStatus] = useState('');
  const [appVersion, setAppVersion] = useState('');
  const [iosVersion, setIosVersion] = useState('');
  const [fatalOnly, setFatalOnly] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);

  const buildFilterParams = () => {
    const params = new URLSearchParams();
    if (crashType) params.set('crashType', crashType);
    if (reviewStatus) params.set('reviewStatus', reviewStatus);
    if (appVersion) params.set('appVersion', appVersion);
    if (iosVersion) params.set('iosVersion', iosVersion);
    if (fatalOnly) params.set('fatalOnly', 'true');
    return params;
  };

  const { data: summaryData } = useQuery(['crash-reports', 'summary'], () =>
    apiFetch(`${API_BASE}/api/crash-reports/summary`)
  );

  const { data: groupData, isLoading: groupsLoading } = useQuery(
    ['crash-reports', 'groups', crashType, reviewStatus, appVersion, iosVersion, fatalOnly],
    () => {
      const params = buildFilterParams();
      params.set('limit', '100');
      return apiFetch(`${API_BASE}/api/crash-reports/groups?${params.toString()}`);
    },
    { enabled: tab === 0 }
  );

  const { data: listData, isLoading: listLoading } = useQuery(
    ['crash-reports', 'list', page, rowsPerPage, crashType, reviewStatus, appVersion, iosVersion, fatalOnly],
    () => {
      const params = buildFilterParams();
      params.set('page', String(page + 1));
      params.set('limit', String(rowsPerPage));
      return apiFetch(`${API_BASE}/api/crash-reports?${params.toString()}`);
    },
    { enabled: tab === 1 }
  );

  const summary = summaryData?.summary;
  const groups = groupData?.groups || [];
  const reports = listData?.items || [];
  const totalReports = listData?.pagination?.total || 0;

  const resetFilters = () => {
    setCrashType('');
    setReviewStatus('');
    setAppVersion('');
    setIosVersion('');
    setFatalOnly(false);
    setPage(0);
  };

  const refreshing = groupsLoading || listLoading;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
        <BugIcon color="error" />
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          Crash Reports
        </Typography>
        <Button
          startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={() => queryClient.invalidateQueries(['crash-reports'])}
          disabled={refreshing}
        >
          Refresh
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Crashes reported by the app on customer devices. Reports are delivered from each
        company&apos;s own server, so they arrive even if the app was force-closed.
      </Alert>

      {summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            ['Reports (7 days)', summary.last7Days],
            ['Fatal (7 days)', summary.fatalLast7Days],
            ['Companies affected (7 days)', summary.companiesAffectedLast7Days],
            ['Untriaged', summary.reviewCounts?.new ?? 0],
            ['Investigating', summary.reviewCounts?.investigating ?? 0],
            ['All reports', summary.total],
          ].map(([label, value]) => (
            <Grid item xs={6} md={4} lg={2} key={label}>
              <Card>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">
                    {label}
                  </Typography>
                  <Typography variant="h5">{value}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Crash type</InputLabel>
            <Select label="Crash type" value={crashType} onChange={(e) => setCrashType(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {Object.entries(CRASH_TYPE_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Triage</InputLabel>
            <Select
              label="Triage"
              value={reviewStatus}
              onChange={(e) => setReviewStatus(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {Object.entries(REVIEW_STATUS_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            label="App version"
            value={appVersion}
            onChange={(e) => setAppVersion(e.target.value)}
            sx={{ width: 140 }}
          />
          <TextField
            size="small"
            label="iOS version"
            value={iosVersion}
            onChange={(e) => setIosVersion(e.target.value)}
            sx={{ width: 140 }}
          />

          <FormControlLabel
            control={
              <Switch checked={fatalOnly} onChange={(e) => setFatalOnly(e.target.checked)} />
            }
            label="Fatal only"
          />

          <Button onClick={resetFilters}>Clear</Button>
        </Stack>
      </Paper>

      <Paper>
        <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label={`Crashes (${groups.length})`} />
          <Tab label="All reports" />
        </Tabs>

        {tab === 0 && (
          <>
            {groupsLoading ? (
              <LoadingSpinner />
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Crash</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Reports</TableCell>
                      <TableCell align="right">Companies</TableCell>
                      <TableCell>Shipped in</TableCell>
                      <TableCell>Last seen</TableCell>
                      <TableCell>Triage</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {groups.map((group) => (
                      <TableRow
                        key={group.signature}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setSelectedGroup(group)}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {group.exceptionName || group.signal || CRASH_TYPE_LABELS[group.crashType]}
                          </Typography>
                          {group.exceptionReason && (
                            <Typography variant="caption" color="text.secondary">
                              {group.exceptionReason.length > 90
                                ? `${group.exceptionReason.slice(0, 90)}…`
                                : group.exceptionReason}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={CRASH_TYPE_LABELS[group.crashType] || group.crashType}
                            color={CRASH_TYPE_COLORS[group.crashType] || 'default'}
                          />
                        </TableCell>
                        <TableCell align="right">{group.reportCount}</TableCell>
                        <TableCell align="right">{group.companyCount}</TableCell>
                        <TableCell>
                          {(group.versions || []).filter(Boolean).slice(0, 3).join(', ') || '—'}
                        </TableCell>
                        <TableCell>{formatWhen(group.lastOccurredAt)}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={REVIEW_STATUS_LABELS[group.reviewStatus] || group.reviewStatus}
                            color={REVIEW_STATUS_COLORS[group.reviewStatus] || 'default'}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Open">
                            <IconButton size="small">
                              <BugIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                    {groups.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">
                            No crashes match these filters.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}

        {tab === 1 && (
          <>
            {listLoading ? (
              <LoadingSpinner />
            ) : (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Crash</TableCell>
                        <TableCell>Company</TableCell>
                        <TableCell>App</TableCell>
                        <TableCell>iOS</TableCell>
                        <TableCell>Device</TableCell>
                        <TableCell>When</TableCell>
                        <TableCell>Triage</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reports.map((report) => (
                        <TableRow key={report._id} hover>
                          <TableCell>
                            <Typography variant="body2">
                              {report.exceptionName ||
                                report.signal ||
                                CRASH_TYPE_LABELS[report.crashType]}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {report.isFatal ? 'Fatal' : 'Non-fatal'} ·{' '}
                              {CRASH_TYPE_LABELS[report.crashType] || report.crashType}
                            </Typography>
                          </TableCell>
                          <TableCell>{report.companyName || report.companyId}</TableCell>
                          <TableCell>{report.appVersion || '—'}</TableCell>
                          <TableCell>{report.iosVersion || '—'}</TableCell>
                          <TableCell>{report.deviceModel || '—'}</TableCell>
                          <TableCell>{formatWhen(report.occurredAt)}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={
                                REVIEW_STATUS_LABELS[report.reviewStatus] || report.reviewStatus
                              }
                              color={REVIEW_STATUS_COLORS[report.reviewStatus] || 'default'}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                      {reports.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                            <Typography color="text.secondary">
                              No crash reports match these filters.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={totalReports}
                  page={page}
                  onPageChange={(_, value) => setPage(value)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[25, 50, 100]}
                />
              </>
            )}
          </>
        )}
      </Paper>

      {selectedGroup && (
        <CrashGroupDialog group={selectedGroup} onClose={() => setSelectedGroup(null)} />
      )}
    </Box>
  );
};

export default CrashReports;
